import React, { FC } from 'react';
import { ADD_PANEL } from '../../constants/HelpIds';
import type { ErrorStateProps, ErrorStateInnerProps } from './ErrorState.types';
import { ErrorReason } from '@wix/add-panel-common';
import classnames from 'classnames';
import { Button, TextLabel } from '@wix/wix-base-ui';
import { BIComponent, BIOrigin } from '../../constants/BI';
import { useActionsDispatcher, ActionsDispatcherProvider } from '../ActionsDispatcher';
import { useUtils, UtilsProvider } from '../UtilsProvider';
import styles from './ErrorState.scss';
import { symbol as Symbol } from '@wix/santa-editor-symbols';
import { HelpActionInitiator } from '../ActionsDispatcher/ActionsDispatcher.types';

const settings = {
  [ErrorReason.AddPanelInternal]: {
    title: 'Add_Panel_Error_Server_Title',
    text: 'Add_Panel_Error_Server_Text',
    tryAgain: 'Add_Panel_Error_Server_CTA',
    learnMore: 'Add_Panel_Error_Server_Link',
    image: <Symbol name="internalErrorIcon" />,
    helpId: ADD_PANEL.ERROR_STATE.SERVER_ERROR,
  },
  [ErrorReason.AddPanelInternalNoStatus]: {
    title: 'Add_Panel_Error_Client_Title',
    text: 'Add_Panel_Error_Client_Text',
    tryAgain: 'Add_Panel_Error_Client_CTA',
    learnMore: 'Add_Panel_Error_Client_Link',
    image: <Symbol name="internalErrorIcon" />,
    helpId: ADD_PANEL.ERROR_STATE.CLIENT_ERROR,
  },
  [ErrorReason.NetworkConnection]: {
    title: 'Add_Panel_Error_Internet_Title',
    text: 'Add_Panel_Error_Internet_Text',
    tryAgain: 'Add_Panel_Error_Internet_CTA',
    learnMore: '',
    image: <Symbol name="networkErrorIcon" />,
    helpId: '',
  },
};

export const ErrorStateInner: FC<ErrorStateInnerProps> = ({ error }) => {
  const { translate } = useUtils();
  const { dispatchHelpClick, dispatchReloadData } = useActionsDispatcher();

  let errorSettings = settings[ErrorReason.AddPanelInternalNoStatus];
  let errorText = translate(errorSettings.text);

  if (error?.response) {
    errorSettings = settings[ErrorReason.AddPanelInternal];
    errorText = translate(errorSettings.text, { status_code: `${error?.response?.status}` });
  } else if (error?.request) {
    errorSettings = settings[ErrorReason.NetworkConnection];
    errorText = translate(errorSettings.text);
  }

  const handleHelpClick = () =>
    dispatchHelpClick({
      helpId: errorSettings.helpId,
      categoryId: '',
      initiator: HelpActionInitiator.ErrorState,
      biHelpParams: {
        learn_more: true,
        component: BIComponent.ErrorState,
        panel_name: BIOrigin.AddPanel,
        origin: BIOrigin.AddPanel,
      },
    });

  return (
    <div data-hook="error-state" className={styles.errorState}>
      <div className={styles.image}>{errorSettings.image}</div>
      <TextLabel
        type="T09"
        value={translate(errorSettings.title)}
        className={styles.title}
        shouldTranslate={false}
        enableEllipsis={false}
      />

      <TextLabel
        type="T01"
        value={errorText}
        className={classnames('btn-md', styles.text)}
        shouldTranslate={false}
        enableEllipsis={false}
      />

      {errorSettings.tryAgain && (
        <Button onClick={dispatchReloadData} className={classnames('btn-md', styles.tryAgain)}>
          {translate(errorSettings.tryAgain)}
        </Button>
      )}

      {errorSettings.learnMore && (
        <Button onClick={handleHelpClick} className="btn-text btn-sm">
          {translate(errorSettings.learnMore)}
        </Button>
      )}
    </div>
  );
};

export const ErrorState: FC<ErrorStateProps> = ({ error, utils, onAction }) => (
  <UtilsProvider utils={utils}>
    <ActionsDispatcherProvider onAction={onAction}>
      <ErrorStateInner error={error} />
    </ActionsDispatcherProvider>
  </UtilsProvider>
);
